import { useState } from 'react';
import { useAppStore } from '../../services/store';
import type { App } from '../../services/types';

export default function CreateApp() {
  const { apps, setApps } = useAppStore();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Productivity',
    accessType: 'Free' as 'Free' | 'Free Trial' | 'Paid',
    price: 0,
    coverImage: '',
    launchUrl: '',
    isHighlighted: false,
    isActive: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const newApp: App = {
      id: Date.now().toString(),
      ...formData,
      rating: 0,
      reviewCount: 0,
      downloads: 0,
      status: 'Not Installed',
      lastUpdated: new Date().toISOString().split('T')[0],
      reviews: [],
      screenshots: [],
      changelog: [],
      roadmap: [],
      features: []
    };

    const updatedApps = [...apps, newApp];
    setApps(updatedApps);

    // Reset form
    setFormData({
      name: '',
      description: '',
      category: 'Productivity',
      accessType: 'Free',
      price: 0,
      coverImage: '',
      launchUrl: '',
      isHighlighted: false,
      isActive: true
    });

    setIsSubmitting(false);
    alert('App created successfully!');
  };

  const categories = ['Productivity', 'Monitoring', 'Governance', 'Analytics', 'Citizen Services', 'AI Tools', 'Integrations', 'Communication'];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create New App</h3>
        <p className="text-gray-600">Add a new application to your App Store</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter app name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            required
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Describe what the app does and its key features"
            maxLength={500}
          />
          <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500 characters</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Type *
            </label>
            <select
              required
              value={formData.accessType}
              onChange={(e) => setFormData({ ...formData, accessType: e.target.value as any })}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
            >
              <option value="Free">Free</option>
              <option value="Free Trial">Free Trial</option>
              <option value="Paid">Paid</option>
            </select>
          </div>

          {formData.accessType === 'Paid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cover Image URL *
          </label>
          <input
            type="url"
            required
            value={formData.coverImage}
            onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://readdy.ai/api/search-image?query=modern%20office%20workspace%20productivity&width=400&height=240&seq=1&orientation=landscape"
          />
          <p className="text-xs text-gray-500 mt-1">
            Use Stable Diffusion format: https://readdy.ai/api/search-image?query=your-description&width=400&height=240&seq=unique-id&orientation=landscape
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Launch URL
          </label>
          <input
            type="url"
            value={formData.launchUrl}
            onChange={(e) => setFormData({ ...formData, launchUrl: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://app.example.com (optional)"
          />
          <p className="text-xs text-gray-500 mt-1">
            URL to launch the app. Leave empty if the app doesn't have a web interface.
          </p>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isHighlighted}
              onChange={(e) => setFormData({ ...formData, isHighlighted: e.target.checked })}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Feature this app</span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Activate immediately</span>
          </label>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Creating App...
              </>
            ) : (
              <>
                <i className="ri-add-line mr-2"></i>
                Create App
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
        <h4 className="font-medium text-blue-900 mb-2">
          <i className="ri-information-line mr-2"></i>
          Cover Image Guidelines
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use descriptive prompts for better image generation</li>
          <li>• Recommended size: 400x240 pixels (landscape orientation)</li>
          <li>• Include background descriptions for consistency</li>
          <li>• Focus on the app's purpose and functionality</li>
        </ul>
      </div>
    </div>
  );
}
